// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase/init';
import {
	signOut,
	GoogleAuthProvider,
	signInWithPopup,
	onAuthStateChanged,
	setPersistence,
	browserSessionPersistence
} from 'firebase/auth';


// Configure Google provider with a domain restriction
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
	hd: 's360digital.com'
});

// Set persistence to session only (clear on browser close)
setPersistence(auth, browserSessionPersistence).catch((error) => {
	console.error("Error setting auth persistence:", error);
});

// Create context and export hook first to prevent circular dependencies
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [hasExplicitlyLoggedIn, setHasExplicitlyLoggedIn] = useState(() => {
		// Initialize from sessionStorage on component mount
		return sessionStorage.getItem('hasExplicitlyLoggedIn') === 'true';
	});
	const navigate = useNavigate();
	const location = useLocation();

	// Method to get Firebase ID token for server API requests
	const getIdToken = async (forceRefresh = false) => {
		if (!auth.currentUser) {
			throw new Error('No authenticated user');
		}

		try {
			return await auth.currentUser.getIdToken(forceRefresh);
		} catch (error) {
			console.error('Error getting ID token:', error);
			throw error;
		}
	};

	// Auth state listener
	useEffect(() => {
		console.log("Setting up auth state listener, hasExplicitlyLoggedIn:", hasExplicitlyLoggedIn);

		const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
			console.log("Auth state changed, user:", firebaseUser?.email, "hasExplicitlyLoggedIn:", hasExplicitlyLoggedIn);

			if (firebaseUser) {
				try {
					// Verify domain
					if (!firebaseUser.email.endsWith('@s360digital.com')) {
						console.log("Non-company email detected, signing out");
						await signOut(auth);
						setUser(null);
						setIsAuthenticated(false);
						setLoading(false);
						return;
					}

					// Basic user info
					const userInfo = {
						email: firebaseUser.email,
						displayName: firebaseUser.displayName || '',
						photoURL: firebaseUser.photoURL || '',
						uid: firebaseUser.uid
					};

					// Only consider the user authenticated if they've explicitly logged in
					// Need to recheck sessionStorage here to avoid race condition
					const isExplicitlyLoggedIn = hasExplicitlyLoggedIn || sessionStorage.getItem('hasExplicitlyLoggedIn') === 'true';

					if (isExplicitlyLoggedIn) {
						console.log("User explicitly logged in, proceeding with authentication");
						setIsAuthenticated(true);
						setUser(userInfo);
						setHasExplicitlyLoggedIn(true);

						// Navigate if needed (now that we know the user is authenticated)
						if (location.pathname === '/login') {
							navigate('/dashboard');
						}
					} else {
						// User has credential but hasn't explicitly logged in during this session
						console.log("User has credential but hasn't explicitly logged in");
						await signOut(auth);
						setUser(null);
						setIsAuthenticated(false);
						navigate('/login');
					}

					// Complete loading state
					setLoading(false);

				} catch (error) {
					console.error('Error processing authentication:', error);
					setUser(null);
					setIsAuthenticated(false);
					setLoading(false);
				}
			} else {
				console.log("Auth state: User is NOT authenticated");
				setUser(null);
				setIsAuthenticated(false);
				setLoading(false);
			}
		});

		return () => {
			unsubscribe();
		};
	}, [navigate, location.pathname, hasExplicitlyLoggedIn]);


	// Function to enable login with Google
	const loginWithGoogle = async () => {
		try {
			setLoading(true);

			// Set the flag BEFORE authenticating with Google
			// This ensures the flag is set before the auth state changes
			sessionStorage.setItem('hasExplicitlyLoggedIn', 'true');
			setHasExplicitlyLoggedIn(true);

			const result = await signInWithPopup(auth, googleProvider);
			console.log("Google sign-in successful for:", result.user?.email);
			return { success: true };
		} catch (error) {
			console.error('Google sign-in error:', error);

			// Clear flag on error
			sessionStorage.removeItem('hasExplicitlyLoggedIn');
			setHasExplicitlyLoggedIn(false);

			// Handle specific error codes
			if (error.code === 'auth/popup-closed-by-user') {
				return {
					success: false,
					error: 'Sign-in was cancelled'
				};
			}

			// Handle unauthorized domain error
			if (error.code === 'auth/unauthorized-domain') {
				return {
					success: false,
					error: 'This domain is not authorized for OAuth operations'
				};
			}

			// Return error message
			return {
				success: false,
				error: 'Failed to sign in with Google'
			};
		} finally {
			setLoading(false);
		}
	};


	const logout = async () => {
		try {
			await signOut(auth);
			sessionStorage.removeItem('hasExplicitlyLoggedIn');
			setHasExplicitlyLoggedIn(false);
			navigate('/');
		} catch (error) {
			console.error('Logout error:', error);
			sessionStorage.removeItem('hasExplicitlyLoggedIn');
			setHasExplicitlyLoggedIn(false);
			setUser(null);
			setIsAuthenticated(false);
			navigate('/');
		}
	};

	// Use a lighter loading indicator
	if (loading && !user) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	const authValues = {
		isAuthenticated,
		user,
		logout,
		loading,
		loginWithGoogle,
		getIdToken
	};

	return (
		<AuthContext.Provider value={authValues}>
			{children}
		</AuthContext.Provider>
	);
};
